<template>
  <div class="flex flex-col justify-start gap-8">
    <div class="flex flex-col gap-5 border-b border-romanSilver pb-5">
      <div class="flex flex-col justify-start gap-2">
        <p class="text-sm text-darkPurple">Category Name</p>
        <div
          class="h-10 flex items-center px-5 bg-cultured border border-romanSilver rounded"
        >
          <p class="text-base text-darkPurple">{{ activeTabItem.name }}</p>
        </div>
      </div>
    </div>
    <div class="flex flex-col justify-start gap-5">
      <h2 class="font-bold text-lg text-darkPurple">Leave Settings</h2>
      <div class="flex flex-col justify-start gap-3">
        <h3 class="text-base font-semibold text-darkPurple">Leave Status:</h3>
        <c-radio
          class="c-radio"
          space-between="mr-2 ml-2"
          :disabled="isViewMode"
          :value="setting.isEnabled"
          @input="updateField('isEnabled', $event)"
          :options="[
            { name: 'Enabled', value: true },
            { name: 'Disabled', value: false },
          ]"
        />
      </div>
      <div class="flex flex-col justify-start gap-3 w-4/6">
        <div class="flex flex-col justify-start gap-0">
          <h3 class="text-base font-semibold text-darkPurple">
            Delegation of Authority:
          </h3>
          <p class="text-xs text-romanSilver">
            Is Delegation of authority mandatory for this leave type?
          </p>
        </div>
        <c-radio
          class="c-radio"
          space-between="mr-2 ml-2"
          :value="setting.hasDelegation"
          @input="updateField('hasDelegation', $event)"
          :disabled="isViewMode"
          :options="[
            { name: 'Yes', value: true },
            { name: 'No', value: false },
          ]"
        />
      </div>
      <div class="flex flex-col justify-start gap-3" v-if="isAnnualLeave">
        <div class="flex flex-col justify-start gap-0">
          <h3 class="text-base font-semibold text-darkPurple">
            Eligible for allowance:
          </h3>
          <p class="text-xs text-romanSilver">
            Is this leave type eligible for leave allowance?
          </p>
        </div>
        <c-radio
          class="c-radio"
          space-between="mr-2 ml-2"
          :value="setting.eligibility"
          @input="updateField('eligibility', $event)"
          :disabled="isViewMode"
          :options="[
            { name: 'Yes', value: true },
            { name: 'No', value: false },
          ]"
        />
        <div
          v-if="
            isViewMode && setting.eligibility === true && setting.paySettingId
          "
        >
          <label class="text-sm text-darkPurple mb-2 block"
            >Select Allowance</label
          >
          <div
            class="h-10 flex items-center px-5 bg-cultured border border-romanSilver rounded"
          >
            <p class="text-base text-darkPurple">
              {{ getPaySettingName(setting.paySettingId) || "---" }}
            </p>
          </div>
        </div>
        <ValidationProvider
          v-else-if="
            setting.eligibility === true && paySettingOptions.length > 0
          "
          name="Select Allowance"
          rules="required"
          v-slot="{ errors }"
        >
          <c-select
            label="Select Allowance"
            placeholder="Select Allowance"
            :options="paySettingOptions"
            :value="setting.paySettingId"
            @input="updateField('paySettingId', $event)"
            :disabled="isViewMode"
          />
          <small v-if="errors[0]" class="text-flame">{{ errors[0] }}</small>
        </ValidationProvider>
        <div
          v-if="setting.eligibility === true && paySettingOptions.length === 0"
          class="w-full px-5 py-2 flex gap-2 rounded-md bg-yellow-50 border border-carrotOrange"
        >
          <Icon icon-name="icon-info" size="xs" class="text-carrotOrange" />
          <span class="text-jet w-full rounded-md text-left text-xs">
            You don't have any leave pay item in pay group 1 and pay group 2 and
            as such employees in these pay groups do not have any leave
            allowance associated with them.
          </span>
        </div>
      </div>
      <div class="flex flex-col justify-start gap-3">
        <div class="flex flex-col justify-start gap-0">
          <h3 class="text-base font-semibold text-darkPurple">
            Calculate days on prorated basis
          </h3>
          <p class="text-xs text-romanSilver">
            Enables poration based on employment date.
          </p>
        </div>
        <c-radio
          class="c-radio"
          space-between="mr-2 ml-2"
          :value="setting.hasPoration"
          @input="updateField('hasPoration', $event)"
          :disabled="isViewMode"
          :options="[
            { name: 'Yes', value: true },
            { name: 'No', value: false },
          ]"
        />
      </div>
      <div class="flex flex-col justify-start gap-3" v-if="isAnnualLeave">
        <div class="flex flex-col justify-start gap-0">
          <h3 class="text-base font-semibold text-darkPurple">
            Allow Leave Carry Forward
          </h3>
          <p class="text-xs text-romanSilver">
            Enables unused leave days to be carried forward based on the
            configured limit.
          </p>
        </div>
        <c-radio
          class="c-radio"
          space-between="mr-2 ml-2"
          :value="setting.hasCarryForward"
          @input="updateField('hasCarryForward', $event)"
          :disabled="isViewMode"
          :options="[
            { name: 'Yes', value: true },
            { name: 'No', value: false },
          ]"
        />
      </div>
      <div class="flex flex-col justify-start gap-3">
        <div class="flex flex-col justify-start gap-0">
          <h3 class="text-base font-normal text-darkPurple">
            Are Public holidays part of leave days
          </h3>
        </div>
        <c-radio
          class="c-radio"
          space-between="mr-2 ml-2"
          :value="setting.includePublicHoliday"
          @input="updateField('includePublicHoliday', $event)"
          :disabled="isViewMode"
          :options="[
            { name: 'Yes', value: true },
            { name: 'No', value: false },
          ]"
        />
      </div>
      <div class="flex flex-col justify-start gap-3">
        <div class="flex flex-col justify-start gap-0">
          <h3 class="text-base font-normal text-darkPurple">
            Enforce handover note policy for leave request
          </h3>
        </div>
        <c-radio
          class="c-radio"
          space-between="mr-2 ml-2"
          :value="setting.handoverNote"
          @input="updateField('handoverNote', $event)"
          :options="[
            { name: 'Yes', value: true },
            { name: 'No', value: false },
          ]"
        />
      </div>
      <div class="line"></div>

      <div class="flex flex-col justify-start gap-5">
        <div class="flex justify-between items-center">
          <h2 class="font-bold text-lg text-darkPurple uppercase">
            ACCRUAL SETTINGS
          </h2>
          <SwitchToggle
            :value="setting.hasAccrual"
            @input="updateField('hasAccrual', $event)"
            :disabled="isViewMode"
          />
        </div>

        <div
          v-if="!setting.hasAccrual"
          class="w-full px-5 py-3 flex gap-2 rounded-md"
          style="background-color: #fff9e6; border: 1px solid #ffe5b4"
        >
          <Icon
            icon-name="icon-info"
            size="xs"
            class="text-carrotOrange flex-shrink-0"
          />
          <span class="text-jet w-full rounded-md text-left text-xs">
            Enable this setting to automate periodic leave accrual according to
            your chosen policy. If disabled, employees will receive their full
            annual leave entitlement as a lump sum at the start of the year.
          </span>
        </div>

        <div
          v-else
          class="w-full px-5 py-3 flex gap-2 rounded-md"
          style="background-color: #fff9e6; border: 1px solid #ffe5b4"
        >
          <Icon
            icon-name="icon-info"
            size="xs"
            class="text-carrotOrange flex-shrink-0"
          />
          <span class="text-jet w-full rounded-md text-left text-xs">
            Leave accrual settings are now enabled for your account. Please
            choose your preferred configuration or toggle off to disable this
            feature.
          </span>
        </div>

        <div
          v-if="setting.hasAccrual"
          class="flex flex-col justify-start gap-5"
        >
          <div class="w-full">
            <div v-if="isViewMode">
              <label class="text-sm text-darkPurple mb-2 block"
                >Accrual frequency</label
              >
              <div
                class="h-10 flex items-center px-5 bg-cultured border border-romanSilver rounded"
              >
                <p class="text-base text-darkPurple">
                  {{
                    getAccrualFrequencyName(setting.accrualFrequency) || "---"
                  }}
                </p>
              </div>
            </div>
            <c-select
              v-else
              label="Accrual frequency"
              placeholder="Select frequency"
              :options="accrualFrequencyOptions"
              :value="setting.accrualFrequency"
              @input="updateField('accrualFrequency', $event)"
              :disabled="isViewMode"
              :rules="['required']"
            />
            <p
              v-if="setting.accrualFrequency === 'daily'"
              class="text-xs text-romanSilver mt-1"
            >
              Employees earn leave for everyday worked
            </p>
            <p
              v-else-if="setting.accrualFrequency === 'monthly'"
              class="text-xs text-romanSilver mt-1"
            >
              Employees earn leave at the start or end of each completed month.
            </p>
            <p
              v-else-if="setting.accrualFrequency === 'dailyPostMonthly'"
              class="text-xs text-romanSilver mt-1"
            >
              Employees earn leave daily after each completed month.
            </p>
          </div>

          <div
            v-if="
              setting.accrualFrequency === 'monthly' ||
              setting.accrualFrequency === 'dailyPostMonthly'
            "
          >
            <div
              class="h-10 flex items-center px-5 bg-cultured border border-romanSilver rounded"
            >
              <p class="text-base text-darkPurple">
                Last day of the month
              </p>
            </div>
          </div>

          <div class="flex flex-col justify-start gap-3" v-feature-request>
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-2">
                <h3 class="text-base font-semibold text-darkPurple">
                  Enable leave overdraw
                </h3>
                <div
                  @mouseenter="showOverdrawTooltip = true"
                  @mouseleave="showOverdrawTooltip = false"
                  class="relative"
                >
                  <Icon
                    icon-name="icon-info"
                    size="xs"
                    class="text-flame cursor-pointer"
                  />
                  <ToolTip
                    v-if="showOverdrawTooltip"
                    style="width: 300px !important"
                    left="-100px"
                  >
                    <p class="w-full mx-auto text-xs text-white text-center">
                      Employees may request leave beyond their current accrued
                      balance, resulting in a temporary negative leave balance.
                      Any negative balance will be automatically offset by
                      future leave accruals.
                    </p>
                  </ToolTip>
                </div>
              </div>
              <SwitchToggle
                :value="setting.hasLeaveOverdraw"
                @input="updateField('hasLeaveOverdraw', $event)"
                :disabled="isViewMode"
              />
            </div>

            <div
              v-if="setting.hasLeaveOverdraw"
              class="w-full flex flex-col justify-start gap-2"
            >
              <label class="date-label">
                Max no of days beyond accrued days
              </label>
              <div
                v-if="isViewMode"
                class="flex justify-start items-center gap-2"
              >
                <div
                  class="w-28 h-10 flex items-center px-5 bg-cultured border border-romanSilver rounded"
                >
                  <p class="text-base text-darkPurple">
                    {{
                      setting.leaveOverdrawDaysMax !== null &&
                      setting.leaveOverdrawDaysMax !== undefined &&
                      setting.leaveOverdrawDaysMax !== ""
                        ? setting.leaveOverdrawDaysMax
                        : "---"
                    }}
                  </p>
                </div>
                <div
                  class="w-28 h-11 flex justify-center items-center px-5 bg-cultured border border-romanSilver rounded"
                >
                  <p class="text-base text-darkPurple font-semibold">Days</p>
                </div>
              </div>
              <validation-provider
                v-else
                name="Max no of days beyond accrued days"
                rules="required"
                v-slot="{ errors }"
                class="w-full flex flex-col justify-start gap-2"
              >
                <div class="flex justify-start items-center gap-2">
                  <c-text
                    class="w-28 applicable-days"
                    placeholder="0"
                    type="number"
                    min="1"
                    v-model="setting.leaveOverdrawDaysMax"
                    :disabled="isViewMode"
                  />
                  <div
                    class="w-28 h-11 flex justify-center items-center px-5 bg-cultured border border-romanSilver rounded"
                  >
                    <p class="text-base text-darkPurple font-semibold">Days</p>
                  </div>
                </div>
                <small v-if="errors.length > 0" class="text-desire">{{
                  errors[0]
                }}</small>
                <small
                  v-else-if="
                    setting.leaveOverdrawDaysMax &&
                    Number(setting.leaveOverdrawDaysMax) < 1
                  "
                  class="text-desire"
                >
                  The minimum value is 1
                </small>
                <small
                  v-else-if="hasOverdrawExceededEntitlement"
                  class="text-desire"
                >
                  Leave overdraw cannot exceed the entitlement days for any job
                  level. Maximum entitlement: {{ maxEntitlementDays }} days
                </small>
              </validation-provider>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import CRadio from "@scelloo/cloudenly-ui/src/components/radio";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import Icon from "@/components/Icon";
import SwitchToggle from "@/components/Switch";
import ToolTip from "@/components/ToolTip";

export default {
  name: "LeaveSettingsStep",
  components: {
    ValidationProvider,
    CSelect,
    CRadio,
    CText,
    Icon,
    SwitchToggle,
    ToolTip,
  },
  props: {
    setting: {
      type: Object,
      required: true,
    },
    activeTabItem: {
      type: Object,
      required: true,
    },
    levelOptions: {
      type: Array,
      default: () => [],
    },
    paySettingOptions: {
      type: Array,
      default: () => [],
    },
    isViewMode: {
      type: Boolean,
      default: false,
    },
    accrualFrequencyOptions: {
      type: Array,
      default: () => [
        { name: "Accrue daily", id: "daily" },
        { name: "Accrue monthly", id: "monthly" },
        { name: "Accrue daily post monthly", id: "dailyPostMonthly" },
      ],
    },
    monthlyAccrueDateOptions: {
      type: Array,
      default: () => [],
    },
    leaveCategory: {
      type: Object,
      default: () => null,
    },
    selectedLevels: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showOverdrawTooltip: false,
    };
  },
  computed: {
    isAnnualLeave() {
      if (!this.leaveCategory) return false;
      const categoryName = (this.leaveCategory.name || "").toLowerCase().trim();
      return categoryName === "annual leave";
    },
    maxEntitlementDays() {
      if (!this.selectedLevels || this.selectedLevels.length === 0) return null;
      const entitlementDays = this.selectedLevels
        .map((level) => {
          const days = level.maximumApplicableDays;
          return days !== null && days !== undefined && days !== ""
            ? Number(days)
            : null;
        })
        .filter((days) => days !== null);

      return entitlementDays.length > 0 ? Math.min(...entitlementDays) : null;
    },
    hasOverdrawExceededEntitlement() {
      if (!this.setting.hasLeaveOverdraw || !this.setting.leaveOverdrawDaysMax)
        return false;
      if (!this.maxEntitlementDays) return false;

      const overdrawDays = Number(this.setting.leaveOverdrawDaysMax);
      return overdrawDays > this.maxEntitlementDays;
    },
    isFormValid() {
      const requiredFields = [
        this.setting.isEnabled,
        this.setting.hasDelegation,
        this.setting.hasPoration,
        this.setting.includePublicHoliday,
        this.setting.handoverNote,
      ];

      if (this.isAnnualLeave) {
        requiredFields.push(
          this.setting.eligibility,
          this.setting.hasCarryForward
        );
      }

      return requiredFields.every(
        (field) => field !== null && field !== undefined
      );
    },
  },
  watch: {
    isFormValid: {
      immediate: true,
      handler(isValid) {
        this.$emit("form-validity-changed", isValid);
      },
    },
  },
  methods: {
    normalizeBoolean(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === "boolean") return value;
      if (typeof value === "string") {
        if (value.toLowerCase() === "true") return true;
        if (value.toLowerCase() === "false") return false;
      }
      return Boolean(value);
    },
    updateSetting(key, value) {
      const updatedSetting = {
        ...this.setting,
        [key]: value,
      };
      this.$emit("update:setting", updatedSetting);
    },
    updateField(key, value) {
      const booleanFields = [
        "isEnabled",
        "hasDelegation",
        "eligibility",
        "hasPoration",
        "allowCarryForward",
        "includePublicHoliday",
        "hasAccrual",
        "hasLeaveOverdraw",
        "handoverNote",
      ];

      const normalizedValue = booleanFields.includes(key)
        ? this.normalizeBoolean(value)
        : value;

      this.updateSetting(key, normalizedValue);
    },
    onSelectedLevels(items) {
      this.$emit("selected-levels", items);
    },
    hasSpecificModule(module) {
      return this.$hasSpecificModule ? this.$hasSpecificModule(module) : false;
    },
    getPaySettingName(paySettingId) {
      if (!paySettingId) return null;
      const option = this.paySettingOptions.find(
        (opt) => opt.id === paySettingId || opt.value === paySettingId
      );
      return option ? option.name || option.nameOnPayslip : null;
    },
    getAccrualFrequencyName(frequencyId) {
      if (!frequencyId) return null;
      const option = this.accrualFrequencyOptions.find(
        (opt) => opt.id === frequencyId
      );
      return option ? option.name : null;
    },
    getMonthlyAccrueDateName(dateTypeId) {
      if (!dateTypeId) return null;
      const option = this.monthlyAccrueDateOptions.find(
        (opt) => opt.id === dateTypeId
      );
      return option ? option.name : null;
    },
  },
};
</script>

<style scoped>
.c-radio {
  width: 100% !important;
  display: flex !important;
  justify-content: flex-start !important;
  gap: 20px !important;
  font-size: 16px !important;
  color: #333333 !important;
}

.line {
  border: 1px solid #c2c7d6;
  width: 100%;
  height: 0;
  margin: 8px auto;
  border-radius: 10px;
  padding: 0;
}

.date-label {
  display: block;
  font-size: 14px;
  font-weight: 400;
  color: #333333;
  margin-bottom: 8px;
}
</style>
